import { useMemo } from 'react'
import { useWatch } from 'react-hook-form'

//UI

import { Card } from '@/components/Elements'

//Constant, Helper
import { camelToWords, numberWithCommas, roundNumber } from '@/utils/stringHelper'
import { sumArray } from '@/utils/arrayHelper'
import { getTotalPrice } from '@/features/offers/helpers/getTotalContractPrice'
import { resolveSubSystemMargin } from '@eapproval/offer-shared'
import { Box } from '@chakra-ui/react'
import { createColumnHelper } from '@tanstack/react-table'
import EditableTable from '@/components/Table/EditableTable'
import { PriceCell } from '@/components/Table/components/Cell'
import { NormalTextCellRenderer } from '@/components/Table/components/Cell/ToolTipCell'

const BookingTabContractPriceSection = () => {
  const subSystems = useWatch({ name: 'subSystems' })
  const exchangeRate = useWatch({ name: 'exchangeRate' })

  const columnHelper = createColumnHelper()

  const bookingPriceData = useMemo(() => {
    if (!subSystems || subSystems.length === 0) {
      return 0
    }

    return subSystems.map((subSystem, index) => {
      const rate = parseFloat(exchangeRate) || 1

      const quotationPrice = getTotalPrice(subSystem.totalContractPrice.newContractRows)
      const quotationPriceHKD = quotationPrice * rate
      const quotationMargin = resolveSubSystemMargin({
        priceHKD: quotationPriceHKD,
        totalCost: subSystem.totalContractPrice.totalCost,
        storedGrossMargin: subSystem.totalContractPrice.grossMargin,
      })

      const bookingPrice = getTotalPrice(subSystem.bookingContractPrices)
      const bookingPriceHKD = bookingPrice * rate
      const bookingMargin = resolveSubSystemMargin({
        priceHKD: bookingPriceHKD,
        totalCost: subSystem?.bookingTotalCost,
        storedGrossMargin: subSystem?.bookingGrossMargin,
      })

      return {
        name: `1.${index + 1}:  ${subSystem.projectName}`,
        quotationPrice: quotationPriceHKD,
        quotationGM: quotationMargin.gmPercentage,
        quotationGMPrice: quotationMargin.gmAmount,
        bookingPrice: bookingPriceHKD,
        bookingGM: bookingMargin.gmPercentage,
        bookingGMPrice: bookingMargin.gmAmount,
      }
    })
  }, [subSystems, exchangeRate])

  const columnDef = useMemo(
    () => [
      columnHelper.group({
        id: 'generalInfo',
        header: () => <span></span>,
        columns: [
          columnHelper.accessor('name', {
            header: () => <span></span>,
            footer: () => <span>Total</span>,
          }),
        ],
      }),

      columnHelper.group({
        id: 'quotation',

        header: () => <span>Offer Price</span>,

        columns: [
          columnHelper.accessor('quotationPrice', {
            header: () => <span>Total Price(HKD)</span>,
            cell: PriceCell,
            footer: ({ table, column: { id: columnId } }) => {
              const totalSubSystemPrice = sumArray(
                table.getCoreRowModel().flatRows.map((row) => row.getValue(columnId))
              )

              return <span>${numberWithCommas(totalSubSystemPrice)}</span>
            },
          }),
          columnHelper.accessor('quotationGM', {
            header: () => <span>GM (%)</span>,

            cell: ({ getValue }) => <span>{roundNumber(getValue(), 2)}%</span>,
            footer: ({ table, column: { id: columnId } }) => {
              const GMPriceTotal = sumArray(
                table.getCoreRowModel().flatRows.map((row) => row.getValue('quotationGMPrice'))
              )
              const priceTotal = sumArray(table.getCoreRowModel().flatRows.map((row) => row.getValue('quotationPrice')))

              if (priceTotal === 0) {
                return <span>0.00%</span>
              }

              const GMPercentageTotal = roundNumber((GMPriceTotal / priceTotal) * 100, 2)

              return <span>{GMPercentageTotal}%</span>
            },
          }),
          columnHelper.accessor('quotationGMPrice', {
            header: () => <span>GM (HKD)</span>,
            cell: PriceCell,
            footer: ({ table, column: { id: columnId } }) => {
              const GMPriceTotal = sumArray(
                table.getCoreRowModel().flatRows.map((row) => row.getValue('quotationGMPrice'))
              )

              return <span>${numberWithCommas(GMPriceTotal)}</span>
            },
          }),
        ],
      }),
      columnHelper.group({
        id: 'booking',
        header: () => <span>Booking Price</span>,
        columns: [
          columnHelper.accessor('bookingPrice', {
            header: () => <span>Total Price(HKD)</span>,
            cell: ({ getValue, row: { original } }) => {
              const quotationPrice = original?.quotationPrice
              const bookingPrice = getValue()
              return (
                <Box color={quotationPrice === bookingPrice ? 'black' : 'red'}>${numberWithCommas(bookingPrice)}</Box>
              )
            },
            footer: ({ table, column: { id: columnId } }) => {
              const totalSubSystemPrice = sumArray(
                table.getCoreRowModel().flatRows.map((row) => row.getValue(columnId))
              )

              return <span>${numberWithCommas(totalSubSystemPrice)}</span>
            },
          }),
          columnHelper.accessor('bookingGM', {
            header: () => <span>GM (%)</span>,
            cell: ({ getValue, row: { original } }) => {
              const quotationGM = original?.quotationGM
              const bookingGM = getValue()
              return <Box color={quotationGM === bookingGM ? 'black' : 'red'}>{roundNumber(bookingGM, 2)}%</Box>
            },
            footer: ({ table, column: { id: columnId } }) => {
              const GMPriceTotal = sumArray(
                table.getCoreRowModel().flatRows.map((row) => row.getValue('bookingGMPrice'))
              )
              const priceTotal = sumArray(table.getCoreRowModel().flatRows.map((row) => row.getValue('bookingPrice')))

              if (priceTotal === 0) {
                return <span>0.00%</span>
              }

              const GMPercentageTotal = roundNumber((GMPriceTotal / priceTotal) * 100, 2)

              return <span>{GMPercentageTotal}%</span>
            },
          }),
          columnHelper.accessor('bookingGMPrice', {
            header: () => <span>GM (HKD)</span>,
            cell: ({ getValue, row: { original } }) => {
              const quotationGMPrice = original?.quotationGMPrice
              const bookingGMPrice = getValue()
              return (
                <Box color={quotationGMPrice === bookingGMPrice ? 'black' : 'red'}>
                  ${numberWithCommas(bookingGMPrice)}
                </Box>
              )
            },
            footer: ({ table, column: { id: columnId } }) => {
              const GMPriceTotal = sumArray(
                table.getCoreRowModel().flatRows.map((row) => row.getValue('bookingGMPrice'))
              )
              return <span>${numberWithCommas(GMPriceTotal)}</span>
            },
          }),
        ],
      }),
    ],
    [columnHelper]
  )

  return (
    <Card title="Booking Price">
      <EditableTable columnDef={columnDef} data={bookingPriceData} disabled={true} showFooter tableWidth="100%" />
    </Card>
  )
}

export default BookingTabContractPriceSection
